import { useEffect, useState } from 'react';
import { TrashIcon, PlusIcon } from '@bizfirst/common-react';
import { useIdentityKeyAdminApiClient } from '../../context/AiAgentAdminApiContext';
import type { IdentityAlgorithm, IdentityKeyDTO, IdentityKeyUsage } from '../../api/identityKeyAdminApiClient';
import { CredentialPicker } from '../Credential/CredentialPicker';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

// Values must match NodeCryptoAlgorithm's constants exactly (see identityKeyAdminApiClient.ts) —
// labels are display-only, friendlier text over the same on-the-wire value.
const ALGORITHMS: IdentityAlgorithm[] = ['Ed25519', 'secp256k1-ethereum', 'secp256k1-bitcoin'];
const ALGORITHM_LABELS: Record<IdentityAlgorithm, string> = {
  'Ed25519': 'Ed25519',
  'secp256k1-ethereum': 'Ethereum (secp256k1)',
  'secp256k1-bitcoin': 'Bitcoin (secp256k1)',
};
const USAGES: IdentityKeyUsage[] = ['Signing', 'KeyAgreement', 'Both'];

/**
 * Manage a node's identity keys inline — launched from the node's own config screen (DID
 * capability tab), not a separate app. Mirrors TeamMemberManager's list+add-row+per-row-action
 * shape. The private key material is never entered here — only a CredentialID reference, picked
 * via CredentialPicker, per this codebase's mandatory credential pattern.
 */
export function IdentityKeyManager({ nodeId }: { nodeId: number }) {
  const api = useIdentityKeyAdminApiClient();
  const [keys, setKeys] = useState<IdentityKeyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newKeyId, setNewKeyId] = useState('');
  const [newAlgorithm, setNewAlgorithm] = useState<IdentityAlgorithm>('Ed25519');
  const [newUsage, setNewUsage] = useState<IdentityKeyUsage>('Signing');
  const [newCredentialId, setNewCredentialId] = useState<number | null>(null);
  const [newIsPrimary, setNewIsPrimary] = useState(false);
  const [adding, setAdding] = useState(false);

  const [pendingRevoke, setPendingRevoke] = useState<IdentityKeyDTO | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await api.getByNodeId(nodeId);
    if (!result.success) setError(result.error?.message ?? 'Failed to load identity keys.');
    setKeys(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const handleAdd = async () => {
    if (!newKeyId.trim() || !newCredentialId) return;
    setAdding(true);
    setError(null);
    const result = await api.create({
      CredentialID: newCredentialId,
      KeyId: newKeyId.trim(),
      Algorithm: newAlgorithm,
      KeyUsage: newUsage,
      VaultProvider: 'software',
      NodeID: nodeId,
      IsPrimary: newIsPrimary,
    });
    setAdding(false);
    if (result.success) {
      setNewKeyId('');
      setNewCredentialId(null);
      setNewIsPrimary(false);
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to create identity key.');
    }
  };

  const handleRevokeConfirmed = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    // v1 simplification: fixed reason text, no custom-reason input (ConfirmDialog's message is
    // plain string only — mirrors its own documented no-restore-in-v1 simplification).
    const result = await api.revoke(pendingRevoke.IdentityKeyID, 'Revoked via node config UI');
    setRevoking(false);
    if (result.success) {
      setPendingRevoke(null);
      await load();
    }
  };

  return (
    <div>
      {error && (
        <div className="error-message" style={{ color: '#ef4444', fontSize: 13, marginBottom: 8, padding: 8, backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: 4 }}>
          {error}
        </div>
      )}

      <div className="aaa-form-grid" style={{ marginBottom: 16 }}>
        <div className="aaa-form-group">
          <label>Key ID</label>
          <input type="text" value={newKeyId} onChange={(e) => setNewKeyId(e.target.value)} placeholder="e.g. key-1" />
        </div>
        <div className="aaa-form-group">
          <label>Algorithm</label>
          <select value={newAlgorithm} onChange={(e) => setNewAlgorithm(e.target.value as IdentityAlgorithm)}>
            {ALGORITHMS.map((a) => (
              <option key={a} value={a}>{ALGORITHM_LABELS[a]}</option>
            ))}
          </select>
        </div>
        <div className="aaa-form-group">
          <label>Usage</label>
          <select value={newUsage} onChange={(e) => setNewUsage(e.target.value as IdentityKeyUsage)}>
            {USAGES.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <CredentialPicker value={newCredentialId} onChange={(id) => setNewCredentialId(id)} label="Credential (private key material)" placeholder="Select or create a credential..." />
        </div>
        <div className="aaa-form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={newIsPrimary} onChange={(e) => setNewIsPrimary(e.target.checked)} />
            Primary key
          </label>
        </div>
        <div className="aaa-form-group" style={{ alignSelf: 'end' }}>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleAdd} disabled={!newKeyId.trim() || !newCredentialId || adding}>
            <PlusIcon size={13} /> {adding ? 'Adding…' : 'Add Key'}
          </button>
        </div>
      </div>

      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && keys.length === 0 && <div className="aaa-entity-dropdown-empty">No identity keys yet.</div>}

      {!loading && keys.length > 0 && (
        <table className="aaa-table">
          <thead>
            <tr>
              <th>Key ID</th>
              <th>Algorithm</th>
              <th>Usage</th>
              <th>Primary</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key.IdentityKeyID}>
                <td>{key.KeyId}</td>
                <td>{key.Algorithm}</td>
                <td>{key.KeyUsage}</td>
                <td>{key.IsPrimary ? 'Yes' : ''}</td>
                <td>
                  <span className={`aaa-badge ${key.IsRevoked ? 'aaa-badge-inactive' : 'aaa-badge-active'}`}>
                    {key.IsRevoked ? 'Revoked' : 'Active'}
                  </span>
                </td>
                <td>
                  {!key.IsRevoked && (
                    <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingRevoke(key)} title="Revoke key">
                      <TrashIcon size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        title="Revoke Identity Key"
        message={`Revoke key "${pendingRevoke?.KeyId}"? It will no longer be usable for signing or encryption.`}
        busy={revoking}
        onConfirm={handleRevokeConfirmed}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
}
